import {z} from "zod/v4-mini"
import {textureTypeSchema} from "#material/nodes/base/material/validation/texture"
import {imageColorSpaceSchema} from "@cm/utils/data-object"
import {exchangeNodeNameSchema} from "#material/exchange/node-name-schema"
import type {SettingValueType} from "#material/sockets/setting-value-type"

export const nodeIdSchema = z.union([
    z.literal("addShader"),
    z.literal("ambientOcclusion"),
    z.literal("brightcontrast"),
    z.literal("bump"),
    z.literal("combineHsv"),
    z.literal("combineRgb"),
    z.literal("combineXYZ"),
    z.literal("diffuseBsdf"),
    z.literal("displacement"),
    z.literal("emission"),
    z.literal("fresnel"),
    z.literal("gamma"),
    z.literal("hueSaturationValue"),
    z.literal("imageTexture"),
    z.literal("imageTextureSet"),
    z.literal("invert"),
    z.literal("layerWeight"),
    z.literal("lightPathNode"),
    z.literal("mapping"),
    z.literal("mathNode"),
    z.literal("mixShader"),
    z.literal("normalMap"),
    z.literal("output"),
    z.literal("principledBsdf"),
    z.literal("rgbCurves"),
    z.literal("rgbMix"),
    z.literal("rgbNode"),
    z.literal("rgbToBw"),
    z.literal("scannedTransmission"),
    z.literal("separateHsv"),
    z.literal("separateRgb"),
    z.literal("separateXYZ"),
    z.literal("setImageTexture"),
    z.literal("shaderToRgb"),
    z.literal("tangent"),
    z.literal("texCoord"),
    z.literal("texGradient"),
    z.literal("texNoise"),
    z.literal("texVoronoi"),
    z.literal("translucentBsdf"),
    z.literal("transparentBsdf"),
    z.literal("uvMap"),
    z.literal("valToRgb"),
    z.literal("value"),
    z.literal("vectorMathNode"),
])

export const parameterIdSchema = z.string()

export const parameterValueSchema = z.union([
    z.string(),
    z.number(),
    z.array(z.number()),
    z.tuple([z.nullable(z.number()), z.nullable(z.number()), z.nullable(z.number())]),
    z.array(z.tuple([z.number(), z.number(), z.number()])),
    z.boolean(),
    z.null(),
])

export const socketValueSchema: z.ZodMiniType<SettingValueType> = z.union([
    z.literal("float"),
    z.literal("color"),
    z.literal("vector"),
    z.literal("string"),
    z.literal("boolean"),
    z.literal("closure"),
    z.literal("colorArray"),
    z.literal("floatArray"),
    z.literal("point"),
    z.literal("normal"),
    z.literal("int"),
    z.literal("euler"),
    z.literal("any"),
])

export const parameterTypeSchema = socketValueSchema

export const parameterSchema = z.object({
    name: z.string(),
    type: z.optional(parameterTypeSchema),
    value: z.optional(parameterValueSchema),
})

export const enumSettingOptionSchema = z.object({value: z.string(), label: z.string()})
export const enumSettingSchema = z.object({
    settingType: z.literal("enum"),
    id: z.string(),
    label: z.string(),
    options: z.array(enumSettingOptionSchema),
})

export const materialNodeDataSchema = z.object({
    id: z.string(),
    legacyId: z.number(),
    name: z.union([exchangeNodeNameSchema, z.literal("Unknown")]),
    settings: z.optional(z.array(enumSettingSchema)),
    textureRevision: z.nullish(
        z.union([
            z.number(),
            z.object({
                imageColorSpace: z.optional(imageColorSpaceSchema),
            }),
        ]),
    ), // this is the TextureRevision id!
    textureSetRevision: z.nullish(
        z.object({
            id: z.string(),
            width: z.number(),
            height: z.number(),
            displacement: z.nullish(z.number()),
            mapAssignments: z.array(
                z.object({
                    textureType: textureTypeSchema,
                    dataObjectLegacyId: z.number(),
                    dataObjectId: z.optional(z.string()),
                }),
            ),
        }),
    ),
    parameters: z.array(parameterSchema),
})

export type NodeId = z.infer<typeof nodeIdSchema>
export type Parameter = z.infer<typeof parameterSchema>
export type ParameterId = z.infer<typeof parameterIdSchema>
export type ParameterType = z.infer<typeof parameterTypeSchema>
export type ParameterValue = z.infer<typeof parameterValueSchema>
export type EnumSettingOption = z.infer<typeof enumSettingOptionSchema>
export type EnumSetting = z.infer<typeof enumSettingSchema>
export type MaterialNodeData = z.infer<typeof materialNodeDataSchema>
